import { useAxios } from '@/hooks/useAxios';
import { Client } from '@stomp/stompjs';
import { defineStore } from 'pinia';
import SockJS from 'sockjs-client';
import { v4 as uuidv4 } from 'uuid'; // uuid 라이브러리 import

const BASE_URL = import.meta.env.VITE_APP_API_URL;

export const useNotificationStore = defineStore('notification', {
    state: () => ({
        notifications: [],
        client: null,
        messageCallback: null,
        connectionAttempts: 0,
        maxConnectionAttempts: 3
    }),
    getters: {
        isConnected: (state) => state.client?.active || false
    },
    actions: {
        async checkServerStatus() {
            try {
                // const response = await fetch(`${BASE_URL}/health`, {
                //     method: 'GET',
                //     mode: 'cors',
                //     credentials: 'include'
                // });

                const url = `/api/manager/my`;
                const config = {
                    method: 'get'
                };
                const { execute } = useAxios(url, config, {
                    immediate: false
                });

                try {
                    const response = await execute();
                    return true;
                } catch (error) {
                    console.error('서버 응답이 비정상적입니다:', error.message);
                    return false;
                }
            } catch (error) {
                console.error('서버 연결 중 오류 발생:', error);
                return false;
            }
        },

        async initializeWebSocket() {
            if (this.isConnected) {
                console.warn('WebSocket already initialized and active.');
                return;
            }

            if (this.connectionAttempts >= this.maxConnectionAttempts) {
                console.error('최대 연결 시도 횟수에 도달했습니다.');
                return;
            }

            // const serverIsReady = await this.checkServerStatus();
            // if (!serverIsReady) {
            //     console.error('서버가 준비되지 않았습니다. WebSocket 연결을 시도하지 않습니다.');
            //     return;
            // }

            this.connectionAttempts++;
            const websocketUrl = `${BASE_URL}/ws/`;
            this.client = new Client({
                webSocketFactory: () =>
                    new SockJS(websocketUrl, null, {
                        transports: ['websocket'], // WebSocket만 사용 (필요에 따라 조정)
                        withCredentials: false // 필요에 따라 true로 변경 (서버 인증 설정에 맞게)
                    }),
                connectHeaders: {
                    // 인증 헤더가 필요한 경우 여기에 추가
                    // Authorization: `Bearer ${yourAuthToken}`,
                },
                reconnectDelay: 5000, // 5초 (필요에 따라 조정)
                heartbeatIncoming: 8000,
                heartbeatOutgoing: 8000,
                onConnect: () => {
                    console.log('STOMP 연결됨');
                    this.connectionAttempts = 0;
                    this.subscribeToNotifications();
                },
                onStompError: (error) => {
                    console.error('STOMP 오류:', error);

                    // if (error.headers && error.headers['message'] && error.headers['message'].includes('CORS')) {
                    //     console.error('CORS 오류가 발생했습니다. 재연결을 시도하지 않습니다.');
                    //     this.corsErrorOccurred = true; // CORS 오류 발생 플래그 설정
                    //     return; // CORS 오류 발생 시 재연결 시도 중단
                    // }

                    if (this.connectionAttempts < this.maxConnectionAttempts) {
                        console.log(`재연결 시도 중... (${this.connectionAttempts}/${this.maxConnectionAttempts})`);
                        setTimeout(() => this.initializeWebSocket(), 5000);
                    }
                },
                onDisconnect: () => {
                    // onDisconnect 콜백
                    console.log('STOMP disconnected');
                },
                onWebSocketClose: () => {
                    // WebSocket close 이벤트 핸들러
                    console.log('STOMP WebSocket closed');
                },
                debug: (str) => {
                    // console.log('STOMP debug:', str); // 디버깅 로깅 (필요에 따라 활성화)
                }
            });

            this.client.activate(); // activate() 호출 (구독은 onConnect에서)
        },

        subscribeToNotifications() {
            // onConnect 콜백 내부에서 호출되므로 연결 확인 불필요
            this.client.subscribe('/topic/alarm', (message) => {
                if (message.body) {
                    const notification = JSON.parse(message.body);
                    // 서버에서 id를 제공하지 않으면 클라이언트에서 생성
                    if (!notification.id) {
                        notification.id = uuidv4(); // 또는 다른 고유 ID 생성 방법 사용
                    }
                    this.addNotification(notification);
                }
            });
        },

        addNotification(notification) {
            try {
                this.notifications.push(notification);
                this.autoRemove(notification.id); // 자동 제거 (5초 후)
                if (this.messageCallback) {
                    this.messageCallback(notification); // 콜백 함수 호출 (알림 처리)
                }
            } catch (error) {
                console.error('알람 처리 중 오류 발생:', error);
                // 오류 로깅 및 사용자에게 오류 메시지 표시 등의 추가적인 처리
            }
        },

        removeNotification(id) {
            this.notifications = this.notifications.filter((n) => n.id !== id);
        },

        autoRemove(id) {
            setTimeout(() => {
                this.removeNotification(id);
            }, 5000); // 5초 (필요에 따라 조정)
        },

        setMessageCallback(callback) {
            this.messageCallback = callback;
        },

        disconnectWebSocket() {
            if (this.client) {
                this.client.deactivate();
                console.log('WebSocket 연결이 해제되었습니다.');
                this.client = null;
            }
        },
        resetConnectionAttempts() {
            this.connectionAttempts = 0;
        }
    }
});
